import * as aor from "react-admin";
import urlJoin from "url-join";

import myRestClient from "./restClient";
import { API_URL } from "../config";

import session, {
    USER_SUBJECT,
    USER_LOGIN_ID,
    USER_COMPANY_ID,
    USER_COMPANY_ROLE,
    USER_COMPANY_ROLE_ID,
    USER_COMPANIES,
    USER_POLICIES
} from "./userSession";

export function getSubject() {
    try {
        const subject = session.get(USER_SUBJECT);
        return subject;
    } catch (err) {
        return null;
    }
}

async function getUserFromUsername(username) {
    const params = {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "username", order: "ASC" },
        filter: { username }
    };
    const result = await myRestClient("GET_LIST", "users", params).then(
        response => {
            if (response.data)
                return response.data.find(element => {
                    return element.username === username;
                });
            else return null;
        },
        err => {
            console.log(err);
            return null;
        }
    );
    return result;
}

export async function getLoggedUser() {
    const subject = getSubject();
    if (subject) {
        const user = await getUserFromUsername(subject);
        return user;
    }
    return null;
}

export async function setLoggedUserIdFromToken() {
    const subject = getSubject();
    if (subject) {
        const user = await getLoggedUser();
        const user_id = user ? user.id : null;
        session.set(USER_LOGIN_ID, user_id);
        return;
    } else {
        session.set(USER_LOGIN_ID, null);
        return;
    }
}

export function getLoggedUserId() {
    return session.get(USER_LOGIN_ID);
}

export function getLoggedUserFromToken() {
    const subject = getSubject();
    if (subject) {
        return subject;
    }
    return null;
}

export function getUserCompanies() {
    const companies = session.get(USER_COMPANIES);
    if (companies) {
        return JSON.parse(companies);
    }
    return [];
}

async function getRoleForCompanyUser(user_id, company_id) {
    const params = {
        user_id,
        company_id
    };
    const result = await myRestClient("GET_ROLE", "role-mappings", params).then(
        response => {
            return response.data;
        },
        err => {
            return null;
        }
    );
    return result;
}

export async function getPermissionsForLoggedUser() {
    if (getSubject()) {
        let myrole = session.get(USER_COMPANY_ROLE_ID);
        if (!myrole) {
            const user_company_id = getUserCompany();
            if (!user_company_id) {
                return "no_role";
            }
            const logged_user = await getLoggedUser();
            const myrole_info = await getRoleForCompanyUser(
                logged_user.id,
                user_company_id
            );
            if (myrole_info) {
                setUserCompanyRole(myrole_info);
                myrole = myrole_info.role_id;
            } else {
                return "no_role";
            }
        }
        if (session.get(USER_POLICIES)) {
            return getPolicies();
        } else {
            const policies = await getRolePolicies(myrole);
            savePolicies(policies);
            return policies;
        }
    } else {
        return "no_role";
    }
}

async function getRolePolicies(role_id) {
    const params = {
        id: role_id
    };
    const result = await myRestClient("GET_ONE", "roles", params);
    return result.data.policies;
}

export function getUserCompanyRole() {
    if (getSubject) {
        return session.get(USER_COMPANY_ROLE_ID);
    } else {
        return "no_role";
    }
}

function savePolicies(policies) {
    session.set(USER_POLICIES, JSON.stringify(policies));
}

export function getPolicies() {
    return session.get(USER_POLICIES);
}

function setUserCompanyRole(role) {
    session.set(USER_COMPANY_ROLE, role.role_description);
    session.set(USER_COMPANY_ROLE_ID, role.role_id);
}

export function setLoginData(subject, userCompanies) {
    session.set(USER_SUBJECT, subject);
    session.set(USER_COMPANIES, userCompanies);
}

export function clearUserCompanies() {
    session.delete(USER_COMPANIES);
}

export function clearAuthToken() {
    session.clear();
}

export function getUserCompany() {
    if (getSubject()) {
        return session.get(USER_COMPANY_ID);
    }
    return null;
}
export async function getUserCompanyWithRefresh() {
    if (getSubject()) {
        return session.get(USER_COMPANY_ID);
    } else {
        if (await refreshSession()) {
            return session.get(USER_COMPANY_ID);
        }
    }
    return null;
}

export async function setUserCompany(company_id) {
    const id = parseInt(company_id, 10);
    const request = new Request(urlJoin(API_URL, "/login"), {
        method: "PATCH",
        mode: "cors",
        body: JSON.stringify({ companyId: id }),
        headers: new Headers({
            "Content-Type": "application/json"
        }),
        credentials: "include" // usato per inviare in cookie httponly invisibile al codice js
    });
    const response = await fetch(request);
    if (response.status < 200 || response.status >= 300) {
        const { message } = await response.json();
        throw new Error(message);
    } else {
        session.set(USER_COMPANY_ID, company_id);
    }
}

export async function refreshSession() {
    const request = new Request(urlJoin(API_URL, "/me"), {
        method: "GET",
        mode: "cors",
        headers: new Headers({
            "Content-Type": "application/json"
        }),
        credentials: "include" // usato per inviare in cookie httponly invisibile al codice js
    });
    const response = await fetch(request);
    if (response.status < 200 || response.status >= 300) {
        return false;
    }
    const {
        id,
        subject,
        companies,
        logged_user_company,
        role,
        role_id,
        policies
    } = await response.json();
    if (id) {
        session.set(USER_LOGIN_ID, id);
        session.set(USER_SUBJECT, subject);
        session.set(USER_COMPANIES, companies);
        session.set(USER_COMPANY_ID, logged_user_company);
        session.set(USER_COMPANY_ROLE, role);
        session.set(USER_COMPANY_ROLE_ID, role_id);
        session.set(USER_POLICIES, policies);
    }
    window.location.reload();
    return true;
}

export default async (type, params) => {
    if (type === aor.AUTH_LOGIN) {
        const { username, password, recaptcha } = params;
        const request = new Request(urlJoin(API_URL, "/login"), {
            method: "POST",
            mode: "cors",
            body: JSON.stringify(
                recaptcha !== null
                    ? { username, password, recaptcha }
                    : { username, password }
            ),
            headers: new Headers({ "Content-Type": "application/json" }),
            credentials: "include"
        });
        const response = await fetch(request);
        if (response.status < 200 || response.status >= 300) {
            const { message } = await response.json();
            throw new Error(message);
        }
        const { subject, user_companies } = await response.json();
        setLoginData(subject, user_companies);
    } else if (type === aor.AUTH_LOGOUT) {
        const request = new Request(urlJoin(API_URL, "/logout"), {
            method: "POST",
            mode: "cors",
            credentials: "include"
        });
        await fetch(request);
        clearAuthToken();
        window.location.reload();
    } else if (type === aor.AUTH_ERROR) {
        const { status } = params;
        // it doesn't log out you anymore if it's a 403 error
        if (status === 401) {
            // || status === 403
            clearAuthToken();
            throw new Error("Unauthorized");
        }
    } else if (type === aor.AUTH_CHECK) {
        if (!getSubject()) {
            if (!(await refreshSession())) {
                throw new Error("No auth token found");
            }
        }
    } else if (type === aor.AUTH_GET_PERMISSIONS) {
        const myrole = await getPermissionsForLoggedUser();
        return myrole;
    } else {
        throw new Error(`authClient: unrecognized type ${type}`);
    }
};



// WEBPACK FOOTER //
// ./src/common/authClient.js